import { API_BASE_URL } from '../config';
import type {
  MetaTargetingSearchResult,
  MetaSavedInterest,
  MetaSavedInterestRequest,
  MetaTargetingStatus,
  MetaSavedInterestsPage,
  MetaHistoryPage,
} from '../types';

export async function fetchMetaTargetingStatus(accessToken: string): Promise<MetaTargetingStatus> {
  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/status`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao verificar status da API Meta');
  }

  const payload = await response.json();
  return payload.data ?? payload;
}

export async function searchMetaTargeting(
  keyword: string,
  locale: string,
  accessToken: string
): Promise<MetaTargetingSearchResult> {
  const params = new URLSearchParams({ q: keyword, locale });
  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/search?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao pesquisar interesses');
  }

  const payload = await response.json();
  return payload.data ?? payload;
}

export async function fetchMetaTargetingSuggestions(
  interests: string[],
  accessToken: string
): Promise<MetaTargetingSearchResult> {
  const params = new URLSearchParams({ interests: interests.join(',') });
  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/suggestions?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao buscar sugestões');
  }

  const payload = await response.json();
  return payload.data ?? payload;
}

export async function fetchMetaTargetingHistory(
  keyword: string,
  page: number,
  size: number,
  accessToken: string
): Promise<MetaHistoryPage> {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (keyword) {
    params.set('keyword', keyword);
  }

  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/history?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar histórico');
  }

  const payload = await response.json();
  return payload.data ?? payload;
}

export async function saveMetaInterest(
  data: MetaSavedInterestRequest,
  accessToken: string
): Promise<MetaSavedInterest> {
  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/saved`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao salvar interesse');
  }

  const payload = await response.json();
  return payload.data ?? payload;
}

export async function fetchSavedMetaInterests(
  page: number,
  size: number,
  accessToken: string
): Promise<MetaSavedInterestsPage> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/saved?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao carregar interesses salvos');
  }

  const payload = await response.json();
  return payload.data ?? payload;
}

export async function deleteMetaSavedInterest(id: string, accessToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/saved/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao excluir interesse salvo');
  }
}

export async function invalidateMetaTargetingCache(keyword: string, accessToken: string): Promise<void> {
  const params = new URLSearchParams({ keyword });
  const response = await fetch(`${API_BASE_URL}/backoffice/meta/targeting/cache/invalidate?${params.toString()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error?.message || 'Erro ao invalidar cache');
  }
}
